/**
 * 진단 상태를 사용자 문구로 바꾼다. 계약(200 + status)의 핵심은 미산출(not_computed)과
 * "데이터 없음"을 구분하는 것이다. 그래서 문구도 상태별로 나눈다. 에피소드 그리드
 * placeholder(상세·리뷰 공용)와 기준 밴드 캡션이 이 문구를 함께 쓴다.
 */

import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import type { DiagnosticsFailureResp, DiagnosticsStatus, DiagnosticsSeriesResp } from "@/api";

/** 에피소드 위상 그리드 자리의 상태 문구(placeholder). */
export const EPISODE_GRID_STATUS_TEXT: Record<DiagnosticsStatus, string> = {
  declined: "이 Dataset은 기준 밴드 집계를 사용하지 않습니다.",
  not_computed: "아직 진단 결과가 산출되지 않았습니다.",
  processing: "진단 결과를 계산하고 있습니다.",
  ready: "진단 결과가 준비되었습니다.",
  partial: "일부 진단 결과만 산출되었습니다.",
  stale: "진단 결과가 최신 상태가 아닙니다.",
  failed: "진단 결과를 계산하지 못했습니다.",
};

// 워커 단계 실패 코드(`<stage>_failed`)를 사유 문구로 바꾼다. 단계 정보가 없는
// 알 수 없는 코드는 상태 문구만 표시한다.
const FAILURE_CODE_TEXT: Record<string, string> = {
  download_failed: "원본을 다운로드하지 못했습니다.",
  assemble_failed: "원본을 파싱하지 못했습니다.",
  compute_failed: "지표를 계산하지 못했습니다.",
  artifact_upload_failed: "결과를 저장하지 못했습니다.",
  report_failed: "결과를 기록하지 못했습니다.",
};

/**
 * 에피소드 그리드 placeholder 문구(상세·리뷰 공용). failed 면 실패 단계 사유와 산출 시도
 * 시각을 병기한다. 그래서 문구는 "왜·언제 실패했는가"까지 알려 준다.
 */
export function episodeGridStatusText(result: {
  status: DiagnosticsStatus;
  failure: DiagnosticsFailureResp | null;
  computedAt: string | null;
}): string {
  let text = EPISODE_GRID_STATUS_TEXT[result.status];
  if (result.status !== "failed") {
    return text;
  }
  const reason = result.failure === null ? null : (FAILURE_CODE_TEXT[result.failure.code] ?? null);
  if (reason !== null) {
    text = `${text} ${reason}`;
  }
  if (result.computedAt !== null) {
    text = `${text} ${formatDateTime(parseUtcMs(result.computedAt))}에 마지막으로 시도했습니다.`;
  }
  return `${text} 잠시 후 재진단을 시도해 주세요.`;
}

/** 기준 밴드가 없을 때 캡션에 덧붙이는 사유 문구. */
export function referenceBandAbsenceText(status: DiagnosticsStatus | null): string {
  switch (status) {
    case "not_computed":
      return "아직 기준 밴드가 산출되지 않았습니다.";
    case "stale":
      return "기준 밴드가 최신 상태가 아닙니다. 관리자 재진단 후 확인해 주세요.";
    case "processing":
      return "기준 밴드를 계산하고 있습니다.";
    case "failed":
      return "기준 밴드를 계산하지 못했습니다. 잠시 후 다시 확인해 주세요.";
    default:
      return "사용할 수 있는 기준 밴드가 없습니다.";
  }
}

/** 파일의 준비·처리 상태를 알려 준다. 관측 결과의 품질 판정과는 관계없다. */
export function diagnosticSeriesStatusText(
  series: DiagnosticsSeriesResp | null,
  label: string,
): string {
  if (series === null) return `${label} 산출물 정보를 찾을 수 없습니다. 다시 확인해 주세요.`;
  switch (series.status) {
    case "not_computed":
      return `${label} 데이터가 아직 산출되지 않았습니다. 관리자 재진단 후 확인해 주세요.`;
    case "stale":
      return `${label} 데이터가 최신 상태가 아닙니다. 관리자 재진단 후 확인해 주세요.`;
    case "failed": {
      const reason =
        series.failure?.code === "motion_upload_failed"
          ? "저장하지 못했습니다."
          : series.failure?.code === "motion_compute_failed"
            ? "계산하지 못했습니다."
            : "처리를 마치지 못했습니다.";
      const code = series.failure === null ? "" : ` (오류 코드: ${series.failure.code})`;
      return `${label} 데이터를 ${reason}${code} 관리자 재진단 후 확인해 주세요.`;
    }
    default:
      return EPISODE_GRID_STATUS_TEXT[series.status];
  }
}
